import { useState, useContext, useEffect, useMemo } from 'react'
import GlobalContext from '../../../../../../../context/global-context'
import LoaderAdm from '../../../../../../../assets/svgIcons/LoaderAdm.json'
import Lottie from 'lottie-react'
import { Select, Button } from '../../../../../ui/index'
import TableTransactions from './Table'
import {
  monthsOptions as months,
  yearsOptions,
  currentYear,
  MERCADOPAGO,
  GETNET,
  TRANSBANK
} from '../../../../../../utils/constant'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import clsx from 'clsx'
import uploadIcon from '../../../../../../../assets/upload.png'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import ModalCargarPagosDigitales from '../ModalCargarPagosDigitales'

import Box from '@mui/material/Box'

import mercadoPagoAuthImage from '../../../../../../../assets/integrations/mercado-pago-auth.png'
import { sileo } from 'sileo'
import { REACT_APP_MERCADOPAGO_REDIRECT_URI } from '../../../../../../utils/constant'
import integrationsAPI from '../../../../../../../api/integrations.api'
import businessApi from '../../../../../../../api/business.api'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import { useInfiniteAdminTransactions } from '../../../../../../../hooks/useInfiniteAdminTransactions'
import { useInfiniteScroll } from '../../../../../../../hooks/useInfiniteScroll'
import { CustomPagination } from '../../../../../table/CustomPagination'
import { useInfiniteDigitalSummary } from '../../../../../../../hooks/useInfiniteDigitalSummary'
import { formatCurrency } from '../../../../../../utils/formatter'
import AdministrationDashboard from '../../dashboard'
import { useQueryClient } from '@tanstack/react-query'
import { useMonthlySummary } from '../../../../../../../queries/useMonthlySummary'
import { QueryKeys } from '../../../../../../../queries/querykeys'
import PlanRestrictedButton from '../../../../../ui/PlanRestrictedButton'

const MP_OPERATION_TYPE_CHIPS = [
  { value: '',                  label: 'Todos' },
  { value: 'pos_payment',       label: 'POS' },
  { value: 'regular_payment',   label: 'Pago Digital' },
  { value: 'recurring_payment', label: 'Pago Suscripción' },
]

const List = () => {
  const [activeTab, setActiveTab] = useState(MERCADOPAGO)
  const [monthFilter, setMonthFilter] = useState(0)
  const [yearFilter, setYearFilter] = useState(currentYear)
  const { currentBusiness } = useContext(GlobalContext)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(true)
  const rowsPerPage = activeTab === MERCADOPAGO ? 250 : 500
  const [order, setOrder] = useState({ col: '', field: '', direction: 'desc' })
  const [modalUploadOpen, setModalUploadOpen] = useState(false)
  const [selectedSource, setSelectedSource] = useState('')
  const [mpOperationType, setMpOperationType] = useState('')
  const [mpAccountFilter, setMpAccountFilter] = useState('')
  const [mpAccounts, setMpAccounts] = useState([])
  const [showMercadoPagoInfoModal, setShowMercadoPagoInfoModal] = useState(false)
  const [showMercadoPagoConnectModal, setShowMercadoPagoConnectModal] = useState(false)
  const [mercadoPagoAccessToken, setMercadoPagoAccessToken] = useState('')
  const [isConnectingMercadoPago, setIsConnectingMercadoPago] = useState(false)
  const [mercadoPagoIntegrated, setMercadoPagoIntegrated] = useState(false)
  const [integrationStatus, setIntegrationStatus] = useState(null)
  const [expandedDays, setExpandedDays] = useState({})
  const queryClient = useQueryClient()
  const providerParam = activeTab === MERCADOPAGO ? 'mercadopago' : activeTab === GETNET ? 'getnet' : 'transbank'
  const { data: monthlySummary, isLoading: isLoadingMonthlySummary } = useMonthlySummary(
    currentBusiness?.id || 0,
    {
      year: yearFilter,
      ...(monthFilter >= 1 && { month: monthFilter }),
      provider: providerParam
    },
    { enabled: !!currentBusiness?.id }
  )
  const providerSummary =
    activeTab === MERCADOPAGO
      ? monthlySummary?.mercadopago
      : activeTab === GETNET
        ? monthlySummary?.getnet
        : monthlySummary?.transbank
  const summaryCardsData = [
    {
      title: 'Total liberaciones',
      amount: formatCurrency(providerSummary?.totalLiberacionesMes ?? 0),
      highlighned: true
    },
    {
      title: 'Total propina',
      amount: formatCurrency(providerSummary?.totalPropinaMes ?? 0),
      highlighned: false
    },
    {
      title: 'Total comisiones pagadas',
      amount: formatCurrency(providerSummary?.totalComisionesPagadasMes ?? 0),
      highlighned: false
    }
  ]

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch
  } = useInfiniteAdminTransactions({
    businessId: currentBusiness?.id || 0,
    organizationId: currentBusiness?.organizationId || 0,
    activeTab,
    year: yearFilter,
    month: monthFilter,
    limit: rowsPerPage,
    filters: {
      searchQuery: debouncedSearchQuery,
      source: selectedSource
    }
  })

  const {
    data: infiniteSummaryData,
    fetchNextPage: fetchNextSummaryPage,
    hasNextPage: hasNextSummaryPage,
    isFetchingNextPage: isFetchingNextSummaryPage,
    isLoading: isSummaryLoading,
    refetch: refetchSummary
  } = useInfiniteDigitalSummary({
    businessId: currentBusiness?.id || 0,
    organizationId: currentBusiness?.organizationId || 0,
    year: yearFilter,
    month: monthFilter,
    limit: 50,
    source: selectedSource,
    operationType: mpOperationType || undefined,
    bankAccountId: mpAccountFilter ? Number(mpAccountFilter) : undefined
  })

  useEffect(() => {
    const fetchMpAccounts = async () => {
      if (!currentBusiness?.id) return
      try {
        const response = await businessApi.getMercadoPagoAccounts(currentBusiness.id)
        setMpAccounts(Array.isArray(response.data) ? response.data : [])
      } catch (error) {
        console.error('Error fetching Mercado Pago accounts:', error)
      }
    }
    fetchMpAccounts()
  }, [currentBusiness?.id])

  const mpAccountOptions = useMemo(() => {
    const options = [{ value: '', label: 'Todas las cuentas' }]
    mpAccounts.forEach(account => {
      options.push({
        value: String(account.id),
        label: account.numeroCuenta
      })
    })
    return options
  }, [mpAccounts])

  useEffect(() => {
    if (activeTab === GETNET) {
      setSelectedSource('getnet_ventas')
    } else if (activeTab === TRANSBANK) {
      setSelectedSource('transbank_cartola')
    } else {
      setSelectedSource('mercadopago_pos')
    }
  }, [activeTab])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    const fetchMercadoPagoStatus = async () => {
      if (!currentBusiness?.id) return

      try {
        const response = await integrationsAPI.getIntegrationsStatus(currentBusiness.id)
        setIntegrationStatus(response.data)
        setMercadoPagoIntegrated(response.data?.mercadopago?.integrated || false)
      } catch (error) {
        console.error('Error fetching Mercado Pago integration status:', error)
      }
    }

    fetchMercadoPagoStatus()
  }, [currentBusiness?.id])

  useEffect(() => {
    const handleMessage = event => {
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'MERCADOPAGO_OAUTH_SUCCESS') {
        sileo.success({
          title: 'Conexión Exitosa',
          description: (
            <span className='text-green-500/80 font-medium'>
              Mercado Pago ha sido conectado exitosamente. Tus transacciones estarán en Luca en unos
              minutos
            </span>
          )
        })
        setShowMercadoPagoInfoModal(false)
        setMercadoPagoIntegrated(true)
        integrationsAPI
          .getIntegrationsStatus(currentBusiness?.id)
          .then(({ data }) => setIntegrationStatus(data))
          .catch(() => {})
        if (refetchSummary) {
          refetchSummary()
        }
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MONTHLY_SUMMARY] })
      } else if (event.data.type === 'MERCADOPAGO_OAUTH_ERROR') {
        sileo.error({
          title: 'Error de Autenticación',
          description: (
            <span className='text-red-500/80 font-medium'>
              {event.data.message || 'Error al conectar con Mercado Pago'}
            </span>
          )
        })
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [refetchSummary, queryClient])

  const handleUploadComplete = async () => {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.ADMIN_TRANSACTIONS] })
    queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MONTHLY_SUMMARY] })
    await refetch()
    if (refetchSummary) {
      await refetchSummary()
    }
  }

  const handleConfirmMercadoPago = async () => {
    if (!currentBusiness?.id) {
      sileo.error({
        title: 'Error de Negocio',
        description: (
          <span className='text-red-500/80 font-medium'>
            No se pudo obtener la información del negocio
          </span>
        )
      })
      return
    }

    try {
      const response = await integrationsAPI.getMercadoPagoOAuthUrl(currentBusiness.id, {
        redirect_uri: REACT_APP_MERCADOPAGO_REDIRECT_URI,
        organization_id: currentBusiness.organizationId
      })

      if (response.data?.url) {
        window.open(response.data.url, '_blank')
      } else {
        sileo.error({
          title: 'Error de Conexión',
          description: (
            <span className='text-red-500/80 font-medium'>
              No se pudo obtener la URL de autorización de Mercado Pago
            </span>
          )
        })
      }
    } catch (error) {
      console.error('Error al conectar con Mercado Pago:', error)
      const errorMessage =
        error?.response?.data?.message ||
        'Error al conectar con Mercado Pago. Por favor, intenta nuevamente.'
      sileo.error({
        title: 'Error de Integración',
        description: <span className='text-red-500/80 font-medium'>{errorMessage}</span>
      })
    }
  }

  const handleConnectMercadoPagoWithToken = async () => {
    if (!currentBusiness?.id) {
      sileo.error({
        title: 'Error de Negocio',
        description: (
          <span className='text-red-500/80 font-medium'>
            No se pudo obtener la información del negocio
          </span>
        )
      })
      return
    }

    if (!mercadoPagoAccessToken.trim()) {
      sileo.error({
        title: 'Token Requerido',
        description: (
          <span className='text-red-500/80 font-medium'>
            Por favor ingresa el access token de Mercado Pago
          </span>
        )
      })
      return
    }

    setIsConnectingMercadoPago(true)

    try {
      await sileo.promise(
        businessApi.connectMercadoPagoWithAccessToken(
          currentBusiness.id,
          mercadoPagoAccessToken.trim()
        ),
        {
          loading: { title: 'Conectando' },
          success: {
            title: '¡Conectado!',
            description: (
              <span className='text-green-500/80 font-medium'>
                Mercado Pago ha sido conectado exitosamente. Tus transacciones estarán en Luca en
                unos minutos
              </span>
            )
          },
          error: {
            title: 'Error de Conexión',
            description: 'Error al conectar Mercado Pago'
          }
        }
      )

      setShowMercadoPagoConnectModal(false)
      setMercadoPagoAccessToken('')
      refetchSummary()
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MONTHLY_SUMMARY] })
    } catch (error) {
      console.error('Error al conectar MercadoPago:', error)
      const errorMessage =
        error?.response?.data?.message ||
        'Error al conectar con MercadoPago. Por favor, intenta nuevamente.'
      sileo.error({
        title: 'Error de Token',
        description: <span className='text-red-500/80 font-medium'>{errorMessage}</span>
      })
    } finally {
      setIsConnectingMercadoPago(false)
    }
  }

  const processedSummaryData = useMemo(() => {
    if (activeTab !== MERCADOPAGO) return []

    const data =
      infiniteSummaryData?.pages
        ?.flatMap(page => page.data)
        .map(item => {
          let source = item.proveedor
          if (item.proveedor === 'MercadoPago') {
            source = 'mercadopago_pos'
          } else if (item.proveedor === 'Transbank') {
            source = 'transbank_cartola'
          } else if (item.proveedor === 'Getnet') {
            source = 'getnet_ventas'
          }

          return {
            fecha: item.fecha,
            montoTotal: item.montoTotal || 0,
            recibidoTotal: item.transaccionRecibida || 0,
            comisionTotal: item.comision || 0,
            propinaTotal: item.propina || 0,
            cantidadTransacciones: item.cantidadTransacciones || 0,
            source: source
          }
        }) || []

    if (!debouncedSearchQuery) return data

    return data.filter(
      item =>
        item.montoTotal?.toString().includes(debouncedSearchQuery) ||
        item.recibidoTotal?.toString().includes(debouncedSearchQuery)
    )
  }, [infiniteSummaryData, activeTab, debouncedSearchQuery])

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage: activeTab === MERCADOPAGO ? hasNextSummaryPage : hasNextPage,
    isFetchingNextPage: activeTab === MERCADOPAGO ? isFetchingNextSummaryPage : isFetchingNextPage,
    fetchNextPage: activeTab === MERCADOPAGO ? fetchNextSummaryPage : fetchNextPage
  })

  const transactions = useMemo(() => {
    return infiniteData?.pages?.flatMap(page => page.data) || []
  }, [infiniteData])

  const totalTransactions = useMemo(() => {
    if (activeTab === MERCADOPAGO) {
      return infiniteSummaryData?.pages?.[0]?.total || 0
    }
    return infiniteData?.pages?.[0]?.total || 0
  }, [infiniteData, infiniteSummaryData, activeTab])

  const handleSort = ({ col, field }) => {
    const newDirection = order.col === col && order.direction === 'asc' ? 'desc' : 'asc'
    setOrder({ col, field, direction: newDirection })
  }

  const sortedTransactions = useMemo(() => {
    const dataToSort = activeTab === MERCADOPAGO ? processedSummaryData : transactions;

    if (!order.field || !dataToSort.length) return dataToSort

    const getValue = (item, field) => {
      if (activeTab === MERCADOPAGO) {
        const summaryMap = {
          monto: 'montoTotal',
          recibido: 'recibidoTotal',
          comision: 'comisionTotal',
          propina: 'propinaTotal',
          status: null
        }
        return item[summaryMap[field] || field] || 0
      }

      // Logic for transactions (tabs 1 and 2)
      switch (field) {
        case 'monto':
          if (item.source?.includes('getnet'))
            return Number(item.rawData?.payload?.valor_venta || 0)
          if (item.source === 'transbank_cartola')
            return Number(item.monto || item.abono || item.rawData?.payload?.monto_afecto || 0)
          return Number(item.monto || 0)

        case 'recibido':
          if (item.source?.includes('getnet'))
            return Number(item.rawData?.payload?.monto_abono || 0)
          if (item.source === 'transbank_cartola')
            return Number(item.abono || item.rawData?.payload?.abono || 0)
          return Number(item.rawData?.payload?.transaction_details?.net_received_amount || 0)

        case 'comision':
          if (item.source?.includes('getnet')) return Number(item.rawData?.payload?.comision || 0)
          if (item.source === 'transbank_cartola') return 0
          return Number(item.rawData?.payload?.fee_details?.[0]?.amount || 0)

        case 'propina':
          if (item.source?.includes('getnet')) return Number(item.rawData?.payload?.propina || 0)
          if (item.source === 'transbank_cartola') return 0
          return Number(item.rawData?.payload?.metadata?.tip_amount || 0)

        case 'pos_id':
          if (item.source?.includes('getnet')) return item.rawData?.payload?.terminal || ''
          if (item.source === 'transbank_cartola')
            return item.rawData?.payload?.codigo_comercio || ''
          return item.rawData?.payload?.pos_id || ''

        case 'local':
          if (item.source?.includes('getnet')) return item.rawData?.payload?.local || ''
          if (item.source === 'transbank_cartola')
            return item.rawData?.payload?.nombre_local || item.nombreLocal || ''
          return item.rawData?.payload?.additional_info?.point_location?.city || ''

        case 'descripcion':
          if (item.source?.includes('getnet'))
            return item.descripcion || item.rawData?.payload?.tipo_mov || ''
          if (item.source === 'transbank_cartola')
            return item.descripcion || item.rawData?.payload?.tipo_de_movimiento || ''
          return item.rawData?.payload?.description || ''

        case 'status':
          if (item.source?.includes('getnet')) return item.rawData?.payload?.estado || ''
          return item.status || item.estado || ''

        case 'fecha': {
          const dateVal = activeTab === GETNET ? item.createdAt || item.fecha : item.fecha
          return new Date(dateVal).getTime()
        }

        case 'fechaAbono':
          return item.rawData?.payload?.fecha_de_abono || item.fechaAbono || ''

        case 'tarjeta':
          return item.rawData?.payload?.card?.last_four_digits || ''

        case 'email':
          return item.rawData?.payload?.payer?.email || ''

        case 'ref_externa':
          return item.rawData?.payload?.external_reference || ''

        case 'tipo_pago_recurrente': {
          const type = item.rawData?.payload?.point_of_interaction?.type || ''
          return type === 'SUBSCRIPTIONS' ? 'Suscripción' : type
        }

        case 'num_cuota':
          return item.rawData?.payload?.point_of_interaction?.transaction_data?.subscription_sequence?.number ?? ''

        case 'nombre':
          return item.rawData?.payload?.card?.cardholder?.name || ''

        default:
          return item[field] || ''
      }
    }

    return [...dataToSort].sort((a, b) => {
      let valA = getValue(a, order.field)
      let valB = getValue(b, order.field)

      if (typeof valA === 'string') valA = valA.toLowerCase()
      if (typeof valB === 'string') valB = valB.toLowerCase()

      if (valA < valB) return order.direction === 'asc' ? -1 : 1
      if (valA > valB) return order.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [transactions, processedSummaryData, order, activeTab])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setSearchQuery('')
    setMpOperationType('')

    if (newValue === GETNET) {
      setSelectedSource('getnet_ventas')
    } else if (newValue === TRANSBANK) {
      setSelectedSource('transbank_cartola')
    } else {
      setSelectedSource('mercadopago_pos')
    }
  }

  const groupedTransactions = useMemo(() => {
    const groups = {}
    const dataToGroup = activeTab === MERCADOPAGO ? processedSummaryData : sortedTransactions;

    dataToGroup.forEach(t => {
      const dateValue = activeTab === GETNET ? t.createdAt || t.fecha : t.fecha
      const date = new Date(dateValue).toLocaleDateString(
        'es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
      if (!groups[date]) groups[date] = []
      groups[date].push(t)
    })
    return groups
  }, [sortedTransactions, processedSummaryData, activeTab])

  const monthOptionsWithAll = useMemo(
    () => [{ value: 0, label: 'Todos los meses' }, ...months],
    [months]
  )

  const [fetchedDetails, setFetchedDetails] = useState({})
  const [loadingDetails, setLoadingDetails] = useState({})

  const handleToggleDay = async displayDate => {
    const dateParts = displayDate.split('-')
    let apiDate = displayDate
    if (dateParts.length === 3 && dateParts[2].length === 4) {
      apiDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
    }
    if (expandedDays[displayDate]) {
      setExpandedDays(prev => ({ ...prev, [displayDate]: false }))
      return
    }

    setExpandedDays(prev => ({ ...prev, [displayDate]: true }))

    if (activeTab === MERCADOPAGO && !fetchedDetails[displayDate]) {
      try {
        setLoadingDetails(prev => ({ ...prev, [displayDate]: true }))

        const response = await businessApi.getDigitalPayments(
          currentBusiness?.id,
          currentBusiness?.organizationId,
          {
            beginDate: `${apiDate}T00:00:00Z`,
            endDate: `${apiDate}T23:59:59Z`,
            limit: 100,
            page: 1,
            operationType: mpOperationType || undefined,
            bankAccountId: mpAccountFilter ? Number(mpAccountFilter) : undefined
          }
        )

        let transactions = []
        if (response.data && Array.isArray(response.data.transactions)) {
          transactions = response.data.transactions
        } else if (Array.isArray(response.data)) {
          transactions = response.data
        } else if (response.data && Array.isArray(response.data.data)) {
          transactions = response.data.data
        } else if (response.data && Array.isArray(response.data.results)) {
          transactions = response.data.results
        }

        setFetchedDetails(prev => ({
          ...prev,
          [displayDate]: transactions
        }))
      } catch (error) {
        console.error('Error fetching details for day:', displayDate, error)
        sileo.error({
          title: 'Error de Carga',
          description: (
            <span className='text-red-500/80 font-medium'>Error al cargar detalles del día</span>
          )
        })
      } finally {
        setLoadingDetails(prev => ({ ...prev, [displayDate]: false }))
      }
    }
  }

  const totalLoadedTransactions = useMemo(() => {
    if (activeTab === MERCADOPAGO) {
      return processedSummaryData.reduce((acc, curr) => acc + (curr.cantidadTransacciones || 0), 0)
    }
    return transactions.length
  }, [processedSummaryData, transactions, activeTab])

  const getRequiredPermission = () => {
    if (activeTab === MERCADOPAGO) return 'allowMercadopagoIntegration'
    if (activeTab === GETNET) return 'allowGetnetMovements'
    if (activeTab === TRANSBANK) return 'allowTransbankMovements'
    return 'allowTransbankMovements'
  }

  useEffect(() => {
    setFetchedDetails({})
    setExpandedDays({})
  }, [yearFilter, monthFilter, activeTab, mpOperationType, mpAccountFilter])

  const formatLastSyncTime = lastSyncAt => {
    if (!lastSyncAt) return null
    try {
      const syncDate = new Date(lastSyncAt)
      const today = new Date()
      const timeZone = 'America/Santiago'
      const getParts = date =>
        Object.fromEntries(
          new Intl.DateTimeFormat('es-CL', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            weekday: 'long',
            hourCycle: 'h23',
          }).formatToParts(date).map(p => [p.type, p.value])
        )
      const syncParts = getParts(syncDate)
      const todayParts = getParts(today)
      const time = `${syncParts.hour}:${syncParts.minute}`
      if (syncParts.day === todayParts.day && syncParts.month === todayParts.month && syncParts.year === todayParts.year) {
        return `hoy a las ${time}`
      }
      const dayName = syncParts.weekday.charAt(0).toUpperCase() + syncParts.weekday.slice(1)
      return `${dayName} ${syncParts.day}/${syncParts.month}/${syncParts.year} a las ${time}`
    } catch (error) {
      return null
    }
  }

  const mercadopagoLastSyncText = integrationStatus?.mercadopago?.lastSyncAt
    ? formatLastSyncTime(integrationStatus.mercadopago.lastSyncAt)
    : null

  return (
    <>
      <AdministrationDashboard
        year={yearFilter}
        customCards={summaryCardsData}
        customLoading={isLoadingMonthlySummary}
      />
      <div
        className='mt-6 bg-white rounded-3xl shadow-md border border-gray-100 flex flex-col min-h-0 overflow-hidden'
        style={{ height: 'calc(100vh - 12rem)', maxHeight: 'calc(100vh - 12rem)' }}
      >
        <div className='flex-shrink-0 px-4 sm:px-6 pt-5'>
          <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>
                {activeTab === MERCADOPAGO ? 'Mercado Pago' : activeTab === GETNET ? 'Getnet' : 'Transbank'}
              </h2>
              <div className='flex items-center gap-2'>
                <p className='text-xs text-gray-500 mt-0.5'>
                  {totalLoadedTransactions > 0
                    ? `${totalLoadedTransactions} transacciones encontradas`
                    : 'No hay transacciones para mostrar'}
                </p>
                {activeTab === MERCADOPAGO && (
                  <span className='text-[10px] text-gray-400 mt-0.5 ml-2 italic'>
                    {mercadopagoLastSyncText &&
                      ` · Última sincronización ${mercadopagoLastSyncText}`}
                  </span>
                )}
              </div>
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              <PlanRestrictedButton
                requiredPermission={getRequiredPermission()}
                keepEnabled={true}
                canAddAddon
                onClick={() => {
                  if (activeTab === MERCADOPAGO) {
                    if (mercadoPagoIntegrated) {
                      const syncText = mercadopagoLastSyncText
                        ? ` Última sincronización ${mercadopagoLastSyncText}.`
                        : ''
                      sileo.info({
                        title: 'Cuenta Vinculada',
                        description: (
                          <span className='text-blue-500/80 font-medium'>
                            Ya tienes integrado Mercado Pago.{syncText}
                          </span>
                        )
                      })
                    } else {
                      setShowMercadoPagoInfoModal(true)
                    }
                  } else {
                    setModalUploadOpen(true)
                  }
                }}
              >
                <Button
                  name='cargar-pago-digital'
                  data-testid='cargar-pago-digital-btn'
                  aria-label='Cargar Pago Digital'
                  className={clsx(
                    'rounded-full text-sm border border-primary !bg-primary !text-white hover:brightness-110 transition-all shadow-sm'
                  )}
                  title={
                    activeTab === MERCADOPAGO
                      ? mercadoPagoIntegrated
                        ? 'Integrado con Mercado Pago'
                        : 'Integrar Mercado Pago'
                      : activeTab === GETNET
                        ? 'Cargar Pago Digital de Getnet'
                        : 'Cargar Pago Digital de Transbank'
                  }
                  imageSrc={activeTab !== MERCADOPAGO ? uploadIcon : undefined}
                  imgStyle={activeTab !== MERCADOPAGO ? 'ml-2 w-4 h-4' : undefined}
                />
              </PlanRestrictedButton>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className='flex items-center gap-1 text-sm text-muted hover:text-primary px-3 py-2 rounded-full hover:bg-primary/5 font-medium transition-colors'
              >
                Filtros
                <KeyboardArrowDownIcon
                  className={clsx('transition-transform', {
                    'rotate-180': showFilters
                  })}
                />
              </button>
            </div>
          </div>

          <Box sx={{ borderBottom: 4, borderColor: 'rgb(var(--border))', mb: 3, flexShrink: 0 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label='Proveedores de pagos digitales'
              variant='scrollable'
              scrollButtons='auto'
              sx={{
                minHeight: 48,
                '& .MuiTabs-indicator': {
                  height: 4,
                  borderRadius: '9999px',
                  backgroundColor: 'rgb(var(--primary))'
                },
                '& .MuiTab-root': {
                  minHeight: 48,
                  px: { xs: 1.5, sm: 2.5 },
                  color: 'rgb(var(--muted))',
                  fontFamily: 'Inter, Nunito, system-ui, sans-serif',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 500,
                  textTransform: 'none'
                },
                '& .MuiTab-root.Mui-selected': {
                  color: 'rgb(var(--primary))',
                  fontWeight: 700
                }
              }}
            >
              <Tab
                value={MERCADOPAGO}
                icon={<CreditCardIcon />}
                label='Mercado Pago'
                iconPosition='start'
              />
              <Tab
                value={GETNET}
                icon={<CreditCardIcon />}
                label='Getnet'
                iconPosition='start'
              />
              <Tab
                value={TRANSBANK}
                icon={<AccountBalanceIcon />}
                label='Transbank'
                iconPosition='start'
              />
            </Tabs>
          </Box>

          {showFilters && (
            <div className='space-y-3 px-1 sm:px-2 mb-4'>
              <div
                className={clsx(
                  'grid grid-cols-1 gap-3',
                  activeTab === MERCADOPAGO ? 'md:grid-cols-4' : 'md:grid-cols-2'
                )}
              >
                <Select
                  key={`year-${yearFilter}`}
                  label='Año'
                  defaultValue={yearFilter}
                  onChange={e => {
                    const selectedYear = Number(e.target.value)
                    setYearFilter(selectedYear)
                  }}
                  options={yearsOptions}
                />
                <Select
                  key={`month-${monthFilter}-${yearFilter}`}
                  label='Mes'
                  defaultValue={monthFilter}
                  onChange={e => setMonthFilter(Number(e.target.value))}
                  options={monthOptionsWithAll}
                />
                {activeTab === MERCADOPAGO && (
                  <Select
                    key={`type-${mpOperationType}`}
                    label='Tipo de operación'
                    defaultValue={mpOperationType}
                    onChange={e => setMpOperationType(e.target.value)}
                    options={MP_OPERATION_TYPE_CHIPS}
                  />
                )}
                {activeTab === MERCADOPAGO && mpAccountOptions.length > 1 && (
                  <Select
                    key={`mp-account-${mpAccountFilter}`}
                    label='Cuenta de Mercado Pago'
                    defaultValue={mpAccountFilter}
                    onChange={e => setMpAccountFilter(e.target.value)}
                    options={mpAccountOptions}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div className='flex-1 min-h-0 overflow-auto px-4 sm:px-6 pb-2'>
          {(activeTab === MERCADOPAGO ? isSummaryLoading : isLoading && transactions.length === 0) ? (
            <div className='flex flex-col justify-center items-center h-64'>
              <Lottie animationData={LoaderAdm} loop style={{ width: 220, height: 220 }} />
              {activeTab === MERCADOPAGO && (
                <p className='text-gray-500 text-sm mt-4'>
                  Este proceso podría tardar unos minutos
                </p>
              )}
            </div>
          ) : (
            <>
              <TableTransactions
                rows={sortedTransactions}
                groupedRows={groupedTransactions}
                order={order}
                onSort={handleSort}
                loading={
                  activeTab === MERCADOPAGO
                    ? isSummaryLoading && !processedSummaryData.length
                    : isLoading && transactions.length === 0
                }
                activeTab={activeTab}
                summaryData={activeTab === MERCADOPAGO ? sortedTransactions : []}
                expandedDays={expandedDays}
                onToggleDay={handleToggleDay}
                fetchedDetails={fetchedDetails}
                loadingDetails={loadingDetails}
                mpOperationType={mpOperationType}
              />
              <div ref={lastElementRef} className='h-10 flex justify-center items-center'>
                {(activeTab === MERCADOPAGO ? isFetchingNextSummaryPage : isFetchingNextPage) && (
                  <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
                )}
              </div>
            </>
          )}
        </div>

        <div className='mx-4 sm:mx-6 mt-auto mb-4 border-t border-gray-100 pt-4 flex-shrink-0'>
          <CustomPagination
            count={totalTransactions}
            loadedCount={totalLoadedTransactions}
            rowsPerPage={rowsPerPage}
            page={(infiniteData?.pages?.[0]?.currentPage || 1) - 1}
            onPageChange={(e, newPage) => {}}
          />
        </div>
      </div>

      <ModalCargarPagosDigitales
        key={activeTab}
        open={modalUploadOpen}
        setOpen={setModalUploadOpen}
        onUploadComplete={handleUploadComplete}
        defaultType={activeTab === MERCADOPAGO ? 'ventas' : activeTab === GETNET ? 'ventas' : 'cartola'}
        allowedTypes={
          activeTab === MERCADOPAGO ? ['ventas'] : activeTab === GETNET ? ['ventas', 'abonos'] : ['cartola']
        }
      />

      {showMercadoPagoInfoModal && (
        <div className='fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4'>
          <div className='bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 w-full max-w-[500px] max-h-[90vh] overflow-y-auto relative font-sans'>
            <h2 className='text-xl font-bold mb-4 text-gray-900 text-center'>
              Conecta tu cuenta de Mercado Pago
            </h2>

            <div className='mb-6 flex justify-center'>
              <img
                src={mercadoPagoAuthImage}
                alt='Mercado Pago Auth'
                className='w-full max-w-md h-auto rounded-lg'
              />
            </div>

            <p className='mb-6 text-sm text-gray-600 text-center leading-relaxed'>
              Se abrirá una nueva pestaña donde deberás indicar tu país y luego iniciar sesión en tu
              Mercado Pago. Luego, serás redirigido aquí nuevamente.
            </p>

            <div className='mb-4 text-center'>
              <button
                type='button'
                onClick={() => {
                  setShowMercadoPagoInfoModal(false)
                  setShowMercadoPagoConnectModal(true)
                }}
                className='text-muted hover:text-primary text-sm font-medium transition-colors'
              >
                ¿Tienes un token de acceso? Haz click aquí
              </button>
            </div>

            <div className='flex flex-col space-y-3'>
              <button
                onClick={handleConfirmMercadoPago}
                className='w-full bg-primary hover:brightness-110 text-white font-semibold py-2.5 px-6 rounded-full transition-all'
              >
                Continuar
              </button>
              <button
                type='button'
                onClick={() => {
                  setShowMercadoPagoInfoModal(false)
                }}
                className='w-full bg-white hover:bg-gray-50 text-gray-600 border border-gray-200 font-semibold py-2.5 px-6 rounded-full transition-colors'
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showMercadoPagoConnectModal && (
        <div className='fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4'>
          <div className='bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 w-full max-w-[500px] max-h-[90vh] overflow-y-auto font-sans'>
            <h2 className='text-xl font-bold mb-4 text-gray-900 text-center'>
              Conectar MercadoPago con Token de Acceso
            </h2>

            <p className='mb-4 text-sm text-gray-600'>
              Si ya conoces cuál es tu token de acceso, copialo y pégalo aquí para poder integrar tu
              cuenta de Mercado Pago.
            </p>

            <div className='mb-6'>
              <textarea
                value={mercadoPagoAccessToken}
                onChange={e => setMercadoPagoAccessToken(e.target.value)}
                placeholder='Pega aquí tu access token...'
                className='w-full px-4 py-3 text-sm border border-gray-300 rounded-2xl focus:ring-1 focus:ring-primary focus:border-primary resize-none'
                rows={2}
                disabled={isConnectingMercadoPago}
              />
            </div>

            <div className='flex flex-col space-y-3'>
              <button
                onClick={handleConnectMercadoPagoWithToken}
                disabled={isConnectingMercadoPago || !mercadoPagoAccessToken.trim()}
                className='w-full bg-primary hover:brightness-110 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-6 rounded-full transition-all'
              >
                {isConnectingMercadoPago ? 'Conectando...' : 'Conectar'}
              </button>
              <button
                type='button'
                onClick={() => {
                  setShowMercadoPagoConnectModal(false)
                  setMercadoPagoAccessToken('')
                }}
                disabled={isConnectingMercadoPago}
                className='w-full bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-600 border border-gray-200 font-semibold py-2.5 px-6 rounded-full transition-colors'
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default List
